import type { RoofModel, RoofFace, Obstruction, ShadeSample } from '../types';

// ============================================================
// SHADE ANALYSIS — Compute real solar access from 3D geometry
//
// Instead of using a simple azimuth-based heuristic, this module:
// 1. Computes sun positions throughout the year for the latitude
// 2. Casts shadows from obstructions and trees onto roof faces
// 3. Samples multiple points per face to build a shade map
// 4. Returns per-face and per-point solar access percentages
//
// Uses a simplified sun-path model (not full NREL SPA) suitable
// for a prototype. Samples 12 representative days × 8 hours = 96
// sun positions for a balance of speed and accuracy.
// ============================================================

interface SunPosition {
  azimuth: number;   // degrees from north (0=N, 90=E, 180=S, 270=W)
  elevation: number; // degrees above horizon
  weight: number;    // weighting factor (higher = more sun energy)
}

interface Vec3 {
  x: number;
  y: number;
  z: number;
}

/**
 * Compute sun positions for 12 representative days of the year.
 * Each day is sampled at 8 hourly intervals (9am-4pm).
 * Returns 96 sun positions with energy weights.
 */
function computeSunPositions(latitude: number): SunPosition[] {
  const positions: SunPosition[] = [];

  // 12 representative days: ~1 per month, weighted by solar energy
  // Solstices and equinoxes + monthly midpoints
  const dayOfYear = [15, 46, 75, 106, 135, 166, 196, 227, 258, 288, 319, 349];
  // Monthly energy weights (summer gets more weight in northern hemisphere)
  const monthWeights = [0.6, 0.7, 0.85, 1.0, 1.1, 1.15, 1.15, 1.1, 1.0, 0.85, 0.7, 0.6];

  // Hours to sample (9am to 4pm = 8 hours)
  const hours = [9, 10, 11, 12, 13, 14, 15, 16];
  const hourWeights = [0.5, 0.7, 0.9, 1.0, 1.0, 0.9, 0.7, 0.5];

  for (let d = 0; d < dayOfYear.length; d++) {
    const day = dayOfYear[d];
    const declination = 23.45 * Math.sin(((360 / 365) * (day - 81)) * Math.PI / 180);

    for (let h = 0; h < hours.length; h++) {
      const hour = hours[h];
      const hourAngle = (hour - 12) * 15; // degrees, 0 at solar noon

      const latRad = latitude * Math.PI / 180;
      const declRad = declination * Math.PI / 180;
      const haRad = hourAngle * Math.PI / 180;

      // Solar elevation
      const sinElev = Math.sin(latRad) * Math.sin(declRad) +
                      Math.cos(latRad) * Math.cos(declRad) * Math.cos(haRad);
      const elevation = Math.asin(sinElev) * 180 / Math.PI;

      // Solar azimuth (clockwise from north)
      const cosAz = (Math.sin(declRad) - Math.sin(latRad) * sinElev) /
                    (Math.cos(latRad) * Math.cos(Math.asin(sinElev)));
      let azimuth = Math.acos(Math.max(-1, Math.min(1, cosAz))) * 180 / Math.PI;
      if (hourAngle > 0) azimuth = 360 - azimuth; // afternoon sun is in the west

      // Skip positions below horizon
      if (elevation <= 0) continue;

      positions.push({
        azimuth,
        elevation,
        weight: monthWeights[d] * hourWeights[h],
      });
    }
  }

  return positions;
}

/**
 * Convert a sun position to a 3D direction vector.
 * Azimuth: 0=N (Y+), 90=E (X+), 180=S (Y-), 270=W (X-)
 * Elevation: 0=horizon, 90=overhead
 */
function sunToDirection(sun: SunPosition): Vec3 {
  const azRad = sun.azimuth * Math.PI / 180;
  const elRad = sun.elevation * Math.PI / 180;
  return {
    x: Math.sin(azRad) * Math.cos(elRad),
    y: Math.cos(azRad) * Math.cos(elRad),
    z: Math.sin(elRad),
  };
}

/**
 * Check if a point on the roof is shaded for a given sun direction.
 * Tests against all obstructions (trees, chimneys) as simple
 * vertical cylinders/boxes that cast shadows.
 */
function isPointShaded(
  point: Vec3,
  sunDir: Vec3,
  obstructions: Obstruction[],
  roofHeight: number
): boolean {
  for (const obs of obstructions) {
    // Model each obstruction as a vertical cylinder of width × height
    const obsBase: Vec3 = { x: obs.position[0], y: obs.position[1], z: 0 };
    const obsTop: Vec3 = { x: obs.position[0], y: obs.position[1], z: obs.height };

    // Project a ray from the point toward the sun
    // and check if it intersects the obstruction's volume

    // Simplified: calculate the shadow the obstruction casts
    // The shadow of the top of the obstruction falls at:
    // shadowPoint = obsTop + sunDir * (obsTop.z / sunDir.z) [scaled to ground level]
    // But we need to check if our point is in the shadow cone

    // For each obstruction, compute the shadow it casts at the roof height
    const dx = point.x - obsBase.x;
    const dy = point.y - obsBase.y;
    const horizontalDist = Math.sqrt(dx * dx + dy * dy);

    // The shadow length depends on sun elevation
    // shadowLength = obstructionHeight / tan(elevation)
    // But we need the shadow at the point's height
    const heightAbovePoint = obs.height - point.z;
    if (heightAbovePoint <= 0) continue; // obstruction is below the point

    const elRad = Math.asin(sunDir.z); // sun elevation in radians
    if (elRad <= 0) continue; // sun below horizon

    // Shadow length at the point's height
    const shadowLength = heightAbovePoint / Math.tan(elRad);

    // Direction from obstruction toward the sun (horizontal)
    const sunHorizLen = Math.sqrt(sunDir.x * sunDir.x + sunDir.y * sunDir.y);
    if (sunHorizLen < 0.001) continue; // sun directly overhead → minimal shadow

    const shadowDirX = sunDir.x / sunHorizLen;
    const shadowDirY = sunDir.y / sunHorizLen;

    // Shadow center at the point's height
    const shadowCenterX = obsBase.x + shadowDirX * shadowLength;
    const shadowCenterY = obsBase.y + shadowDirY * shadowLength;

    // Distance from our point to the shadow center
    const sdx = point.x - shadowCenterX;
    const sdy = point.y - shadowCenterY;
    const distToShadow = Math.sqrt(sdx * sdx + sdy * sdy);

    // If the point is within the obstruction's radius of the shadow center, it's shaded
    if (distToShadow < obs.width / 2) {
      return true;
    }
  }
  return false;
}

/**
 * Compute solar access for a single point on a roof face.
 * Tests against all sun positions and returns the percentage
 * of weighted sun energy that reaches the point.
 */
function computePointSolarAccess(
  point: Vec3,
  sunPositions: SunPosition[],
  obstructions: Obstruction[],
  roofHeight: number
): number {
  let totalWeight = 0;
  let shadedWeight = 0;

  for (const sun of sunPositions) {
    const sunDir = sunToDirection(sun);
    totalWeight += sun.weight;

    if (isPointShaded(point, sunDir, obstructions, roofHeight)) {
      shadedWeight += sun.weight;
    }
  }

  if (totalWeight === 0) return 0;
  return Math.round(((totalWeight - shadedWeight) / totalWeight) * 100);
}

/**
 * Compute solar access for all faces in a roof model.
 * Samples a grid of points per face and returns both
 * per-face average and per-point shade samples.
 */
export function computeShadeAnalysis(
  roofModel: RoofModel,
  latitude: number
): RoofModel {
  if (roofModel.obstructions.length === 0) {
    // No obstructions → no shade to compute, keep existing estimates
    return roofModel;
  }

  const sunPositions = computeSunPositions(latitude);

  const updatedFaces = roofModel.faces.map(face => {
    // Sample a grid of points within the face polygon
    const samples = samplePointsInPolygon(face.vertices, face.height);

    const shadePoints: ShadeSample[] = samples.map(pt => ({
      x: pt.x,
      y: pt.y,
      solar_access: computePointSolarAccess(
        { x: pt.x, y: pt.y, z: face.height * 0.8 },
        sunPositions,
        roofModel.obstructions,
        roofModel.roofHeight
      ),
    }));

    // Average solar access for the face
    const avgAccess = shadePoints.length > 0
      ? Math.round(shadePoints.reduce((s, p) => s + p.solar_access, 0) / shadePoints.length)
      : face.solar_access;

    return {
      ...face,
      solar_access: avgAccess,
      shade_points: shadePoints,
    };
  });

  return {
    ...roofModel,
    faces: updatedFaces,
    shading_basis: 'modeled',
  };
}

/**
 * Sample points in a grid within a polygon.
 * Uses the polygon's bounding box and point-in-polygon test.
 */
function samplePointsInPolygon(
  vertices: [number, number][],
  height: number,
  spacing = 4 // feet between sample points
): { x: number; y: number }[] {
  const xs = vertices.map(v => v[0]);
  const ys = vertices.map(v => v[1]);
  const minX = Math.min(...xs);
  const maxX = Math.max(...xs);
  const minY = Math.min(...ys);
  const maxY = Math.max(...ys);

  const points: { x: number; y: number }[] = [];

  for (let x = minX + spacing / 2; x < maxX; x += spacing) {
    for (let y = minY + spacing / 2; y < maxY; y += spacing) {
      if (pointInPolygon([x, y], vertices)) {
        points.push({ x, y });
      }
    }
  }

  return points;
}

/**
 * Point-in-polygon test using ray casting.
 */
function pointInPolygon(point: [number, number], polygon: [number, number][]): boolean {
  const [px, py] = point;
  let inside = false;

  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const [xi, yi] = polygon[i];
    const [xj, yj] = polygon[j];

    if (((yi > py) !== (yj > py)) &&
        (px < (xj - xi) * (py - yi) / (yj - yi) + xi)) {
      inside = !inside;
    }
  }

  return inside;
}
